'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Container, Typography } from '@mui/material';
import CreditedImage from '@/components/website/CreditedImage';
import { PRINT_PATH } from '@/consts/routes';
import { galleryImageFromApi } from '@/utils/galleryImage';
import buildImageRows from './buildImageRows';

function isLandscape(naturalWidth, naturalHeight) {
  return naturalWidth > naturalHeight;
}

function probeImageOrientation(src) {
  return new Promise(resolve => {
    const img = new window.Image();
    img.onload = () => {
      resolve(isLandscape(img.naturalWidth, img.naturalHeight));
    };
    img.onerror = () => {
      // Treat failed loads as portrait so they still pair rather than solo.
      resolve(false);
    };
    img.src = src;
  });
}

function PrintImageRow({ images }) {
  const visibleImages = images.filter(image => Boolean(image?.src));
  if (visibleImages.length === 0) return null;

  const isPair = visibleImages.length > 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: isPair ? 'row' : 'column' },
        gap: { xs: 2, md: 3 },
        alignItems: 'stretch',
      }}
    >
      {visibleImages.map(image => (
        <Box
          key={image.src}
          sx={{
            flex: isPair ? 1 : undefined,
            width: '100%',
            display: isPair ? 'flex' : 'block',
            overflow: 'hidden',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <CreditedImage
            src={image.src}
            alt={image.alt}
            photographerCredit={image.photographerCredit}
            imgSx={{
              width: '100%',
              display: 'block',
              ...(isPair
                ? { flex: 1, objectFit: 'cover', minHeight: 0, height: '100%' }
                : { height: 'auto' }),
            }}
            sx={isPair ? { flex: 1, display: 'flex', minHeight: 0 } : undefined}
          />
        </Box>
      ))}
    </Box>
  );
}

PrintImageRow.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      photographerCredit: PropTypes.string,
    }),
  ).isRequired,
};

export default function PrintDetailPage({ publication }) {
  const images = useMemo(() => {
    const cover = galleryImageFromApi(
      {
        src: publication.coverSrc,
        photographerCredit: publication.coverPhotographerCredit,
      },
      {
        defaultAlt: `${publication.title} ${publication.date} cover`,
      },
    );

    const gallery = (publication.gallery ?? [])
      .map((entry, index) =>
        galleryImageFromApi(entry, {
          defaultAlt: `${publication.title} interior spread ${index + 1}`,
        }),
      )
      .filter(Boolean);

    return [cover, ...gallery].filter(Boolean);
  }, [
    publication.coverPhotographerCredit,
    publication.coverSrc,
    publication.date,
    publication.gallery,
    publication.title,
  ]);

  const [imageRows, setImageRows] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function classifyAndBuildRows() {
      if (images.length === 0) {
        if (!cancelled) setImageRows([]);
        return;
      }

      const landscapeBySrc = {};
      await Promise.all(
        images.map(async image => {
          landscapeBySrc[image.src] = await probeImageOrientation(image.src);
        }),
      );

      if (!cancelled) {
        setImageRows(buildImageRows(images, landscapeBySrc));
      }
    }

    setImageRows(null);
    classifyAndBuildRows();

    return () => {
      cancelled = true;
    };
  }, [images]);

  return (
    <>
      <Box component="section" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography component="h1" variant="h4" sx={{ fontWeight: 500, mb: 1 }}>
            {publication.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 4 }}>
            {publication.date}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
            {imageRows?.map(row => (
              <PrintImageRow key={row.map(image => image.src).join('-')} images={row} />
            ))}
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Box
            component={Link}
            href={PRINT_PATH}
            sx={{
              display: 'inline-block',
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 500,
              borderBottom: '1px solid currentColor',
              '&:hover': { opacity: 0.7 },
            }}
          >
            Back to Print
          </Box>
        </Container>
      </Box>
    </>
  );
}

PrintDetailPage.propTypes = {
  publication: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    coverSrc: PropTypes.string.isRequired,
    coverPhotographerCredit: PropTypes.string,
    gallery: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          src: PropTypes.string.isRequired,
          alt: PropTypes.string,
          photographerCredit: PropTypes.string,
        }),
      ]),
    ),
  }).isRequired,
};
